// src/pages/operations-dashboard/components/RevenueSection.jsx
import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { IndianRupee, TrendingUp, ArrowUpRight, ArrowDownRight } from "lucide-react";
import {
  formatCurrency,
  formatNumber,
  formatShortDate,
  getChangePercent,
} from "../../../utils/dashboardConstants";

const RevenueTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3">
      <p className="text-xs font-semibold text-gray-500 mb-1.5">{formatShortDate(label)}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-sm font-bold text-gray-800">{formatCurrency(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const BigStatCard = ({ label, value, change, changePrev, icon: Icon, gradient, textColor }) => {
  const pct = getChangePercent(
    typeof value === "string" ? parseFloat(value) : value,
    typeof changePrev === "string" ? parseFloat(changePrev) : changePrev
  );
  const isPositive = pct >= 0;

  return (
    <div className={`relative overflow-hidden rounded-2xl p-5 ${gradient}`}>
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`w-4.5 h-4.5 ${textColor}`} strokeWidth={2} />
          <span className={`text-xs font-semibold ${textColor} opacity-80`}>{label}</span>
        </div>
        <p className={`text-2xl font-bold ${textColor} tracking-tight mb-1`}>{formatCurrency(value)}</p>
        {changePrev !== undefined && changePrev !== null && (
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-flex items-center gap-0.5 px-2 py-0.5 text-xs font-semibold rounded-full ${
                isPositive ? "bg-white/30 text-emerald-800" : "bg-white/30 text-red-800"
              }`}
            >
              {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(pct).toFixed(1)}%
            </span>
            <span className={`text-xs ${textColor} opacity-60`}>vs prev period</span>
          </div>
        )}
      </div>
      {/* Decorative circle */}
      <div className="absolute -right-4 -bottom-4 w-24 h-24 rounded-full bg-white/10" />
      <div className="absolute -right-2 -bottom-2 w-16 h-16 rounded-full bg-white/10" />
    </div>
  );
};

const Skeleton = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded-lg animate-pulse ${className}`} />
);

export default function RevenueSection({ data, summary, loading }) {
  const isLoading = loading?.revenue;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-36" />
          <Skeleton className="h-36" />
          <Skeleton className="h-36" />
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <Skeleton className="h-6 w-48 mb-6" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (!data && !summary) return null;

  const totalRevenue = data?.total_revenue || summary?.revenue_in_period || 0;
  const todayRevenue = summary?.revenue_today || 0;
  const weekRevenue = summary?.revenue_this_week || 0;
  const monthRevenue = summary?.revenue_this_month || 0;

  const dailyData = (data?.daily_revenue || []).map((d) => ({
    date: d.date,
    revenue: typeof d.revenue === "string" ? parseFloat(d.revenue) : d.revenue || 0,
  }));

  return (
    <div className="space-y-4">
      {/* Revenue Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <BigStatCard
          label="Today's Revenue"
          value={todayRevenue}
          changePrev={summary?.revenue_yesterday}
          icon={IndianRupee}
          gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
          textColor="text-white"
        />
        <BigStatCard
          label="This Week"
          value={weekRevenue}
          icon={TrendingUp}
          gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
          textColor="text-white"
        />
        <BigStatCard
          label="This Month"
          value={monthRevenue}
          icon={IndianRupee}
          gradient="bg-gradient-to-br from-violet-500 to-purple-600"
          textColor="text-white"
        />
        <BigStatCard
          label="Period Total"
          value={totalRevenue}
          icon={IndianRupee}
          gradient="bg-gradient-to-br from-amber-400 to-orange-500"
          textColor="text-white"
        />
      </div>

      {/* Revenue Trend */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-50">
              <IndianRupee className="w-4.5 h-4.5 text-emerald-500" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Revenue Trend</h3>
              <p className="text-xs text-gray-400 mt-0.5">Daily revenue over the selected period</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={dailyData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="50%" stopColor="#10b981" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatShortDate}
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatNumber(v)}
                />
                <Tooltip content={<RevenueTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  fill="url(#revenueGradient)"
                  dot={false}
                  activeDot={{ r: 5, fill: "#10b981", stroke: "white", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              No revenue data available for the selected period
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
